'use client'

import React, { Suspense, useEffect, useRef, useState } from 'react'

const VimeoVideo = React.lazy(() => import('./VimeoVideo'))

export type VimeoPlayerProps = {
  videoId: string
  autoPlay?: boolean
  aspectRatio?: number
  className?: string
}

export default function VimeoPlayer({
  videoId,
  autoPlay,
  aspectRatio = 16 / 9,
  className,
}: VimeoPlayerProps) {
  const [isVisible, setIsVisible] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (isVisible) return

    window.addEventListener('scroll', handleScroll)

    function handleScroll() {
      if (window.scrollY >= 20) {
        setIsVisible(true)
        return
      }
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        // Check if the element is intersecting and if we've scrolled at least 20px
        if (entry.isIntersecting) {
          setIsVisible(true)
          observer.disconnect()
        }
      },
      {
        root: null,
        rootMargin: '0px',
        threshold: 0.1,
      },
    )

    if (containerRef.current) {
      observer.observe(containerRef.current)
    }

    return () => {
      window.removeEventListener('scroll', handleScroll)
      observer.disconnect()
    }
  }, [isVisible])

  return (
    <div ref={containerRef} className={className} style={{ aspectRatio }}>
      {isVisible && (
        <Suspense>
          <VimeoVideo videoId={videoId} autoPlay={autoPlay} />
        </Suspense>
      )}
    </div>
  )
}
